import { useContext, useState, useCallback, useEffect, useMemo, ReactNode } from "react";
import {
  LensSDKException,
  type UserBusinessRole,
  type BusinessJoinRequest,
  type BusinessInvitation,
} from "lens-platform-sdk";
import { type BusinessUserWithSeats } from "lens-platform-sdk/dist/cjs/BusinessService";
import { Button, ButtonBar, LoadingIndicator, NumberBadge } from "@k8slens/lds";
import { Redirect, Route, useHistory, Switch } from "react-router-dom";
import clsx from "clsx";

import { useAnalytics } from "src/hooks/useAnalytics";
import { useBusinessUsers } from "src/hooks/useBusinessUsers";
import { getUniqueEmailsArray } from "src/services/email";
import { getSubscriptionSeatCount } from "src/services/getSubscriptionSeatCount";
import { isAssignableSubscription } from "src/services/getSubscriptionState";
import { cap100 } from "src/utils/number";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useGetBusinessInvitations } from "src/hooks/useGetBusinessInvitations";

import { InfoIcon } from "src/components/Icon/Navigation";
import { BusinessContext } from "src/components/Business/Base";
import Notification from "src/components/Notification/Notification";
import { TabGroup, TabList, Tab } from "src/components/Tabs/Tabs";

import AddUsersModal from "./AddUsersModal";
import Pending from "./Pending";
import Requests from "./Requests";
import Users from "./Users";

import styles from "./page.module.css";
import { useDeleteBusinessInvitation } from "src/hooks/useDeleteBusinessInvitation";

const addUsersLabel = "Add Users";
const noSeatsText = "No seats";

const hasActiveSeats = ({ seats }: BusinessUserWithSeats) =>
  seats.some(({ subscription }) => subscription.state === "active" || subscription.state === "canceled");
const isPendingUser = ({ state }: BusinessUserWithSeats) => state === "pending";

const filterActive = (users: Array<BusinessUserWithSeats>) => users.filter((user) => hasActiveSeats(user));
const filterInactive = (users: Array<BusinessUserWithSeats>) =>
  users.filter((user) => !isPendingUser(user) && !hasActiveSeats(user));
const filterPending = (invitations: Array<BusinessInvitation>) =>
  invitations.filter(({ subscriptionId }) => Boolean(subscriptionId));

const views: Array<{
  key: string;
  title: string;
  notifications(d: {
    users: Array<BusinessUserWithSeats>;
    requests: Array<BusinessJoinRequest>;
    invitations: Array<BusinessInvitation>;
  }): ReactNode | null;
  path(businessId: string): string;
}> = [
  {
    key: "active",
    title: "Active",
    notifications: ({ users }) => {
      const n = filterActive(users).length;

      return n ? ` (${cap100(n)})` : null;
    },
    path: (businessId) => `/business/${businessId}/users/active`,
  },
  {
    key: "requests",
    title: "Requests",
    notifications: ({ requests }) => (
      <NumberBadge
        className="ml-md"
        type="error"
        value={requests.length}
        title={`${requests.length} pending request(s).`}
      />
    ),
    path: (businessId) => `/business/${businessId}/users/requests`,
  },
  {
    key: "pending",
    title: "Pending",
    notifications: ({ invitations }) => {
      const n = filterPending(invitations).length;

      return n ? ` (${cap100(n)})` : null;
    },
    path: (businessId) => `/business/${businessId}/users/pending`,
  },
];

const BusinessUsers = () => {
  const history = useHistory();
  const { trackButtonClicked } = useAnalytics();
  const {
    businessId,
    pendingRequests: { requests, updateRequestAsync: updateRequest, errorUpdatingRequest },
  } = useContext(BusinessContext);

  const {
    businessUsers,
    loading: loadingBusinessUsers,
    error: errorLoadingBusinessUsers,
    createInvitesAsync: addBusinessUsers,
    errorCreatingInvites: errorAddingBusinessUsers,
    creatingInvites: addingBusinessUsers,
  } = useBusinessUsers(businessId);

  const {
    businessSubscriptions,
    loading: loadingBusinessSubscriptions,
    reload: reloadBusinessSubscriptions,
  } = useGetBusinessSubscriptions(businessId);
  const {
    businessInvitations,
    loading: loadingBusinessInvitations,
    reload: reloadBusinessInvitations,
  } = useGetBusinessInvitations(businessId);

  const { deleteBusinessInvitation, loading: deleteBusinessInvitationLoading } = useDeleteBusinessInvitation();

  const assignableBusinessSubscriptions = useMemo(
    () => businessSubscriptions.filter(isAssignableSubscription),
    [businessSubscriptions],
  );

  const seatCount = useMemo(
    () => getSubscriptionSeatCount(businessInvitations, assignableBusinessSubscriptions),
    [businessInvitations, assignableBusinessSubscriptions],
  );

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [addUsersModalOpen, setAddUsersModalOpen] = useState(false);
  const openAddUsersModal = useCallback(() => {
    trackButtonClicked(addUsersLabel);
    setAddUsersModalOpen(true);
  }, [trackButtonClicked]);

  const closeAddUsersModal = useCallback(() => {
    setAddUsersModalOpen(false);
  }, []);

  const handleRemoveBusinessInvitation = async (invitation: BusinessInvitation) => {
    if (!businessId || !invitation) {
      return;
    }

    await deleteBusinessInvitation(businessId, invitation.id, "link" in invitation);
    await reloadBusinessInvitations();
  };

  const handleAddUsers = useCallback(
    async (emails: string, subscriptionId: string, role: UserBusinessRole) => {
      const emailArray = getUniqueEmailsArray(emails);

      try {
        await addBusinessUsers(emailArray.map((email) => ({ email, role, subscriptionId })));

        await Promise.all([reloadBusinessSubscriptions(), reloadBusinessInvitations()]);
        closeAddUsersModal();

        // Set active tab to "Pending" to show assigned seat.
        if (activeTabIndex !== 2) {
          setActiveTabIndex(2);
        }

        return null;
      } catch (err) {
        return err as LensSDKException;
      }
    },
    [activeTabIndex, addBusinessUsers, closeAddUsersModal, reloadBusinessInvitations, reloadBusinessSubscriptions],
  );

  // Keep tabs up to date with the URL
  useEffect(() => {
    const tab = views.find((view) => businessId && view.path(businessId) === history.location.pathname);

    if (tab) {
      setActiveTabIndex(views.indexOf(tab));
    }
  }, [history.location.pathname, businessId]);

  const handleTabChange = useCallback(
    (index: number) => {
      if (!businessId) {
        return;
      }
      setActiveTabIndex(index);

      const tab = views[index];

      history.push(tab.path(businessId));
    },
    [history, businessId],
  );

  const activeUsers = filterActive(businessUsers);
  const inactiveUsers = filterInactive(businessUsers);
  const pendingUsers = filterPending(businessInvitations);

  return (
    <>
      <section className={styles.manageUsers}>
        <header className={styles.header}>
          <h2 className={clsx("lds-h2-caps", styles.heading)}>Manage Users</h2>
          {loadingBusinessSubscriptions || loadingBusinessInvitations ? (
            <div className={styles.seatInfo} aria-label="Loading">
              <LoadingIndicator />
            </div>
          ) : (
            <div
              className={styles.seatInfo}
              aria-label={
                seatCount.total === 0
                  ? noSeatsText
                  : `${seatCount.unavailable} out of ${seatCount.total} Lens Pro seats in use`
              }
            >
              {seatCount.total === 0 ? (
                <span className={styles.noSeats} aria-hidden title="Get Seats by clicking the Purchase button">
                  {noSeatsText}
                  <InfoIcon size="sm" />
                </span>
              ) : (
                <>
                  <span
                    className={styles.seatCount}
                    aria-hidden
                  >{`${seatCount.unavailable} / ${seatCount.total}`}</span>
                  <span aria-hidden>Lens Pro seats in use</span>
                </>
              )}
            </div>
          )}
        </header>
        <div>
          {(errorLoadingBusinessUsers || errorUpdatingRequest) && (
            <Notification
              type="flash"
              level="error"
              message={errorLoadingBusinessUsers?.message || errorUpdatingRequest?.message}
            />
          )}
          <TabGroup defaultIndex={0} selectedIndex={activeTabIndex} onChange={handleTabChange}>
            <div className={styles.tabs}>
              <span className={styles.tableSubTitle}>Manage your Lens Business ID users.</span>
              <TabList className={styles.tableTabButtonBar}>
                {views.map((view) => (
                  <Tab
                    key={view.key}
                    label={
                      <>
                        {view.title}
                        {view.notifications({
                          users: businessUsers,
                          requests: requests || [],
                          invitations: businessInvitations,
                        })}
                      </>
                    }
                  />
                ))}
              </TabList>
            </div>
          </TabGroup>
          <Switch>
            <Route path="/business/:businessId/users/active">
              <Users
                caption="Active Users"
                businessUsers={activeUsers}
                loading={loadingBusinessUsers}
                notDataText="No active users on your Lens Business ID!"
              />
            </Route>
            <Route path="/business/:businessId/users/inactive">
              <Users
                caption="Inactive Users"
                businessUsers={inactiveUsers}
                loading={loadingBusinessUsers}
                notDataText="No inactive users on your Lens Business ID!"
              />
            </Route>
            <Route path="/business/:businessId/users/requests">
              {updateRequest ? <Requests requests={requests} updateRequest={updateRequest} /> : null}
            </Route>
            <Route path="/business/:businessId/users/pending">
              <Pending
                businessInvitations={pendingUsers}
                loading={loadingBusinessUsers || deleteBusinessInvitationLoading}
                onRemoveBusinessInvitation={handleRemoveBusinessInvitation}
              />
            </Route>
            <Route>
              {({ match }) => {
                return <Redirect to={`/business/${match?.params.businessId}/users/active`} />;
              }}
            </Route>
          </Switch>
          <ButtonBar className={styles.buttonBar}>
            <Button
              label={addUsersLabel}
              onClick={() => {
                openAddUsersModal();
              }}
              disabled={assignableBusinessSubscriptions.length === 0}
              primary
            />
          </ButtonBar>
        </div>
      </section>
      <AddUsersModal
        open={addUsersModalOpen}
        adding={addingBusinessUsers}
        errorAdding={errorAddingBusinessUsers}
        businessInvitations={businessInvitations}
        loadingBusinessInvitations={loadingBusinessInvitations}
        businessSubscriptions={assignableBusinessSubscriptions}
        loadingBusinessSubscriptions={loadingBusinessSubscriptions}
        onModalClose={closeAddUsersModal}
        addUsers={handleAddUsers}
      />
      {addUsersModalOpen}
    </>
  );
};

export default BusinessUsers;
